// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Initiate a vendor-created invoice transaction with correct customer/provider roles.
// Uses integration SDK server-side so the event owner is always the customer (payer).
export const initiateInvoicePrivileged = body => {
  return post('/api/initiate-invoice-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};
// Algolia search data
export const getAlgoliaData = body => {
  return post('/api/getAlgoliaData', body);
};
export const createAlgoliaData = body => {
  return post('/api/createAlgoliaData', body);
};
export const updateAlgoliaData = body => {
  return post('/api/updateAlgoliaData', body);
};
export const deleteAlgoliaData = body => {
  return post('/api/deleteAlgoliaData', body);
};
export const searchAlgoliaData = body => {
  return post('/api/searchAlgoliaData', body);
};
// sendGird api collection 
export const onSetScheduledMail = body => {
  return post('/api/scheduledMail', body);
};

export const onCancelScheduledMail = body => {
  return post('/api/cancelScheduledMail', body);
};

// clientApi
export const onSendEventMatchMail = body => {
  return post('/api/sendEventMatchMail', body);
};

export const onGetEventBussinessListing = (body) => {
  return post('/api/getEventBussinessListing', body);
}

export const onUpdateEventBidListing = (body) => {
  return post('/api/updateEventBidListing', body);
}


export const onGetCommisonOfUser = (body) => {
  return post('/api/getCommisonOfUser', body);
}

// RFP-Mangelisting Page apis 
export const getListings = body => {
  return post('/api/getListings', body);
};

export const rfpListings = body => {
  return post('/api/rfpListings', body);
};

export const providerDashboardEvents = body => {
  return post('/api/providerDashboardEvents', body);
};

export const actionRFPByAdmin = body => {
  return post('/api/actionRFPByAdmin', body);
};

export const RFPUpdateDetails = body => {
  return post('/api/RFPUpdateDetails', body);
};

export const sendEmailToAdmin = body => {
  return post('/api/sendEmailToAdmin', body);
};

export const getTransaction = body => {
  return post('/api/getTransaction', body);
};

export const listCollections = body => {
  return post('/api/listCollections', body);
};

export const crudEvents = body => {
  return post('/api/crudEvents', body);
};

export const crudEventTypes = body => {
  return post('/api/crudEventTypes', body);
};

export const crudWeights = body => {
  return post('/api/crudWeights', body);
};
export const crudSettings = body => {
  return post('/api/crudSettings', body);
};

// Registers the provider so RFP notifications can find them. Keyed by user,
// because nothing derived from business listings lists providers reliably.
export const syncProvider = body => {
  return post('/api/syncProvider', body);
};

// Copies the provider's declared services onto their business rows in Mongo,
// so server-side RFP matching can read them. Called after a profile save.
export const syncOfferedServices = body => {
  return post('/api/syncOfferedServices', body);
};

export const crudBusinesses = body => {
  return post('/api/crudBusinesses', body);
};

export const crudTransactions = body => {
  return post('/api/crudTransactions', body);
};

export const crudCategories = body => {
  return post('/api/crudCategories', body);
};

export const matchAlgorithm = body => {
  return post('/api/matchAlgorithm', body);
};

export const unsubscribeUserAPI = body => {
  return post('/api/unsubscribe-user', body);
};

export const uploadImageToCloudinary = body => {
  return post('/api/upload-images', body);
};

export const deleteMultipleImagesFromCloudinaryApi = body => {
  return post('/api/delete-images', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Integration API helpers
export const getUserTransactionCount = body => {
  return post('/api/getUserTransactionCount', body);
};

export const updateTransactionMetadata = body => {
  return post('/api/update-transaction-metadata', body);
};

export const appendTransactionIdToListing = body => {
  return post('/api/appendTransactionIdToListing', body);
};

export const getUserBidAcceptedTransactionsWithListing = body => {
  return post('/api/getUserBidAcceptedTransactionsWithListing', body);
};

export const createOwnerReductionInvoice = body => {
  return post('/api/createOwnerReductionInvoice', body);
};

export const respondToReduction = body => {
  return post('/api/respondToReduction', body);
};

export const getOwnerEventDashboardTransactions = body => {
  return post('/api/getOwnerEventDashboardTransactions', body);
};

export const getProviderEventDashboardTransactions = body => {
  return post('/api/getProviderEventDashboardTransactions', body);
};

export const getProviderCompletedTransactions = body => {
  return post('/api/getProviderCompletedTransactions', body);
};

// fetch the declined bid function
export const declineAllUserBidsAPI = body => {
  return post('/api/declineAllUserBids', body);
};

export const acceptInquiryBidSuccessAPI = body => {
  return post('/api/acceptInquiryBidSuccess', body);
};

export const declineInquiryBidAPI = body => {
  return post('/api/declineInquiryBid', body);
};

// fetch new categories
export const fetchNewCategories = body => {
  return post('/api/fetch-new-categories', body);
};

// update listing
export const updateListingForPartialPaymentTransaction = body => {
  return post('/api/updateListingForPartialPayment', body);
}

export const updatePartialPaymentTransition = body => {
  return post('/api/updatePartialPaymentTransition', body);
}